/**
 * Returns focus to the TagPicker's text input after a tag has been removed.
 *
 * Removing a tag re-renders the control (the input element is re-created and, for
 * a focused tag, the previously-focused node disappears), which drops focus to
 * `document.body`. Some pickers additionally trigger Fluent's roving-tabindex to
 * move focus onto an adjacent tag button across several animation frames before
 * it finally lands on `<body>` — a single focus attempt loses that race.
 *
 * So we retry across a handful of frames until the input actually holds focus,
 * re-grabbing it if Fluent's focus shuffle steals it in the meantime. We bail out
 * early once the input is focused, or if focus has deliberately moved to an
 * element outside this control (the user clicked/tabbed elsewhere) — we only
 * fight `<body>` and Fluent's transient tag-button focus, never the user.
 *
 * @param control the `.fui-TagPickerControl` element the input belongs to
 */
export const focusTagPickerInput = (control: Element | null | undefined): void => {
  if (!control) {
    return;
  }

  // ~8 frames (~130ms) is enough to outlast Fluent's multi-frame focus shuffle
  // while staying imperceptible.
  const maxAttempts = 8;
  let attempts = 0;

  const step = () => {
    attempts += 1;
    const input = control.querySelector('input');
    const active = document.activeElement;

    // Focus restored — nothing more to do.
    if (input && active === input) {
      return;
    }

    // Focus deliberately moved outside this control; don't yank it back.
    if (active && active !== document.body && !control.contains(active)) {
      return;
    }

    input?.focus();

    if (attempts < maxAttempts) {
      requestAnimationFrame(step);
    }
  };

  requestAnimationFrame(step);
};
